import {
  ArrowExportRegular,
  ArrowRightRegular,
  ArrowSyncRegular,
  BeakerRegular,
  CheckmarkCircleRegular,
  ChevronRightRegular,
  ClockRegular,
  DatabaseSearchRegular,
  GridRegular,
  InfoRegular,
  ListRegular,
  LockClosedRegular,
  PlayRegular,
  SearchRegular,
  ShieldCheckmarkRegular,
  SparkleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { Button, ProgressBar, Tab, TabList, Textarea } from '@fluentui/react-components';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { Link, NavLink, useParams, useSearchParams } from './router.js';
import type { DaxPreviewRequest, KPIConcept, ScanJob } from '@metricgraph/contracts';
import { queries, type MeasureView, type ResolutionView, type ReviewCaseView } from './api.js';
import {
  CoverageBadge,
  EmptyState,
  ErrorState,
  ExplanationPath,
  LoadingState,
  MetricCard,
  PageHeader,
  SectionTitle,
  StatusBadge,
} from './components.js';
import type { WorkspaceContainerSelection } from './GraphCanvas.js';

const GraphCanvas = lazy(async () => {
  const module = await import('./GraphCanvas.js');
  return { default: module.GraphCanvas };
});

function QueryBoundary<T>({
  query,
  children,
}: {
  query: { data: T; error: Error | null; isLoading: boolean };
  children: (data: NonNullable<T>) => React.ReactNode;
}) {
  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  if (!query.data)
    return (
      <EmptyState
        title="No current evidence"
        body="Run a discovery scan or adjust the active scope."
      />
    );
  return children(query.data);
}

const kpiManagementViews = [
  { to: '/reviews', label: 'Work Queue' },
  { to: '/catalog', label: 'KPI Catalog' },
  { to: '/agent', label: 'Draft & Simulate' },
  { to: '/sandbox', label: 'Sandbox' },
];

function KpiManagementNav() {
  return (
    <nav className="workspace-subnav" aria-label="KPI Management views">
      <span>KPI Management</span>
      {kpiManagementViews.map(({ to, label }) => (
        <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'active' : '')}>
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function GraphExplorerNav() {
  return (
    <nav className="workspace-subnav" aria-label="Graph Explorer views">
      <span>Graph Explorer</span>
      <NavLink to="/graph" className={({ isActive }) => (isActive ? 'active' : '')}>
        3D Graph
      </NavLink>
      <NavLink to="/measures" className={({ isActive }) => (isActive ? 'active' : '')}>
        Measures (2D)
      </NavLink>
    </nav>
  );
}

function propertyText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return 'Recorded evidence';
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function OverviewGovernanceSection() {
  const governance = useQuery({ queryKey: ['governance'], queryFn: queries.governance });
  return (
    <section
      id="governance-health"
      className="overview-governance"
      aria-labelledby="governance-heading"
    >
      <div className="overview-section-heading">
        <div>
          <span className="eyebrow">Measurable governance</span>
          <h2 id="governance-heading">Governance health</h2>
          <p>Control coverage, maturity, and the highest-value actions for this KPI scope.</p>
        </div>
        <Link className="text-link" to="/governance">
          Open detailed controls <ArrowRightRegular />
        </Link>
      </div>
      <QueryBoundary query={governance}>
        {(data) => (
          <div className="overview-governance-grid">
            <article className="panel overview-governance-score">
              <div className="estate-score compact">
                <svg viewBox="0 0 140 140" aria-hidden>
                  <circle cx="70" cy="70" r="57" />
                  <circle
                    className="progress"
                    cx="70"
                    cy="70"
                    r="57"
                    pathLength="100"
                    strokeDasharray={`${data.overallScore} 100`}
                  />
                </svg>
                <div>
                  <strong>{data.overallScore}</strong>
                  <span>Estate health</span>
                </div>
              </div>
              <div>
                <span className="card-kicker">Current maturity</span>
                <h3>
                  Fabric governance maturity {data.maturity.level} · {data.maturity.label}
                </h3>
                <p>{data.maturity.narrative}</p>
              </div>
            </article>
            <article className="panel overview-governance-actions">
              <SectionTitle
                title="Priority control actions"
                action={<StatusBadge tone="warning">Top 3</StatusBadge>}
              />
              {data.priorityActions.slice(0, 3).map((action) => (
                <Link key={action.key} className="governance-action-row" to={action.route}>
                  <span>
                    <strong>{action.title}</strong>
                    <small>{action.impact}</small>
                  </span>
                  <span>
                    {action.score}% <ArrowRightRegular />
                  </span>
                </Link>
              ))}
            </article>
            <article className="panel overview-control-scores">
              <SectionTitle title="Control coverage" />
              {data.scores.slice(0, 5).map((score) => (
                <div key={score.key}>
                  <span>{score.label}</span>
                  <ProgressBar
                    value={score.score / 100}
                    color={score.score < 60 ? 'warning' : 'brand'}
                    aria-label={`${score.label}: ${score.score}%`}
                  />
                  <strong>{score.score}%</strong>
                </div>
              ))}
            </article>
          </div>
        )}
      </QueryBoundary>
    </section>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <nav className="pagination" aria-label="Pagination">
      <span>{total.toLocaleString()} total</span>
      <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <strong>
        Page {page} of {totalPages}
      </strong>
      <Button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </nav>
  );
}

export function OverviewPage() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: queries.overview });
  return (
    <div className="page">
      <PageHeader
        eyebrow="Enterprise KPI scope"
        title="Good morning. Here’s what needs attention."
        description="A governed view of definitions, implementations, evidence, and change across your KPI estate."
        actions={
          <Link className="button-link primary" to="/reviews">
            <SparkleRegular />
            Review priority work
          </Link>
        }
      />
      <QueryBoundary query={overview}>
        {(data) => (
          <>
            <section className="metric-grid" aria-label="Estate summary">
              <MetricCard
                label="Governed KPIs"
                value={data.estate.kpis}
                meta="Governed business definitions"
              />
              <MetricCard
                label="Observed measures"
                value={data.estate.measures}
                meta={`Across ${data.estate.semanticModels} semantic models · ${data.estate.dimensions ?? 0} dimensions`}
                tone="blue"
              />
              <MetricCard
                label="Reports in scope"
                value={data.estate.reports}
                meta={`${Math.round(data.coverage.visualUsage * 100)}% visual evidence · ${data.estate.orphanedReports ?? 0} unlinked`}
                tone="amber"
              />
              <MetricCard
                label="Review work"
                value={data.attention.pendingReviews}
                meta={`${data.attention.conflicts} conflicts · ${data.attention.placeholders ?? 0} placeholders`}
                tone="violet"
              />
            </section>
            <div className="overview-layout">
              <section className="panel attention-panel">
                <SectionTitle
                  title="Priority attention"
                  action={
                    <Link className="text-link" to="/reviews">
                      Open review queue <ArrowRightRegular />
                    </Link>
                  }
                />
                <div className="attention-list">
                  <Link to="/reviews" className="attention-row critical">
                    <span className="attention-icon">
                      <WarningRegular />
                    </span>
                    <span>
                      <strong>{data.attention.conflicts} conflicting implementations</strong>
                      <small>Same KPI identity, materially different logic</small>
                    </span>
                    <StatusBadge tone="danger">High impact</StatusBadge>
                    <ChevronRightRegular />
                  </Link>
                  <Link to="/sandbox" className="attention-row warning">
                    <span className="attention-icon">
                      <ClockRegular />
                    </span>
                    <span>
                      <strong>{data.attention.expiringSandbox} sandbox metric expiring</strong>
                      <small>Decision required before its review date</small>
                    </span>
                    <StatusBadge tone="warning">Due soon</StatusBadge>
                    <ChevronRightRegular />
                  </Link>
                  <Link to="/connections" className="attention-row info">
                    <span className="attention-icon">
                      <DatabaseSearchRegular />
                    </span>
                    <span>
                      <strong>{data.attention.coverageGaps} evidence coverage gaps</strong>
                      <small>Definitions unavailable for restricted reports</small>
                    </span>
                    <StatusBadge tone="info">Partial</StatusBadge>
                    <ChevronRightRegular />
                  </Link>
                </div>
              </section>
              <section className="panel coverage-panel">
                <SectionTitle
                  title="Evidence coverage"
                  action={<CoverageBadge state="PARTIAL" />}
                />
                <div className="coverage-score">
                  <div>
                    <strong>{Math.round(data.coverage.visualUsage * 100)}%</strong>
                    <span>visual usage</span>
                  </div>
                  <svg viewBox="0 0 120 120" aria-hidden>
                    <circle cx="60" cy="60" r="49" />
                    <circle
                      className="progress"
                      cx="60"
                      cy="60"
                      r="49"
                      pathLength="100"
                      strokeDasharray={`${data.coverage.visualUsage * 100} 100`}
                    />
                  </svg>
                </div>
                <div className="coverage-bars">
                  {[
                    ['Estate inventory', data.coverage.inventory],
                    ['Semantic metadata', data.coverage.semanticMetadata],
                    ['Dependencies', data.coverage.dependency],
                  ].map(([label, value]) => (
                    <div key={String(label)}>
                      <span>{label}</span>
                      <b>{Math.round(Number(value) * 100)}%</b>
                      <i>
                        <em style={{ width: `${Number(value) * 100}%` }} />
                      </i>
                    </div>
                  ))}
                </div>
                <p className="coverage-note">
                  <InfoRegular /> Missing visual evidence never becomes evidence that a measure is
                  not a KPI.
                </p>
                {data.coverage.limitationSummary?.length ? (
                  <div className="coverage-risk-list" aria-label="Coverage risk summary">
                    {data.coverage.limitationSummary.slice(0, 3).map((risk) => (
                      <article key={risk.reason} className={`coverage-risk ${risk.severity}`}>
                        <div>
                          <WarningRegular />
                          <strong>{risk.count} affected reports</strong>
                        </div>
                        <p>{risk.reason}</p>
                        {risk.affectedSubjects.length ? (
                          <small>Examples: {risk.affectedSubjects.join(', ')}</small>
                        ) : null}
                        <em>{risk.recommendedAction}</em>
                      </article>
                    ))}
                  </div>
                ) : null}
              </section>
            </div>
            <section className="panel activity-panel">
              <SectionTitle title="Recent governed activity" />
              <div className="activity-grid">
                {data.recentActivity.slice(0, 4).map((event) => (
                  <article key={event.id}>
                    <span className="activity-mark">
                      <CheckmarkCircleRegular />
                    </span>
                    <div>
                      <strong>{event.action.replaceAll('_', ' ').toLowerCase()}</strong>
                      <small>
                        {event.subjectType.replaceAll('_', ' ')} · {formatDate(event.occurredAt)}
                      </small>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </QueryBoundary>
      <OverviewGovernanceSection />
    </div>
  );
}

function KpiCard({ concept }: { concept: KPIConcept }) {
  const definition =
    concept.name === 'Net Revenue'
      ? 'Recognized sales after returns, discounts, and approved allowances.'
      : concept.name === 'Gross Margin %'
        ? 'Gross profit as a share of recognized net revenue.'
        : 'Customers retained from the eligible starting cohort.';
  return (
    <Link to={`/catalog/${concept.id}`} className="kpi-card">
      <div className="kpi-card-top">
        <span className={`domain-icon ${concept.domain.toLowerCase()}`}>
          {concept.domain.slice(0, 1)}
        </span>
        <div>
          <StatusBadge tone="success">{concept.lifecycle}</StatusBadge>
          <StatusBadge>{concept.criticality}</StatusBadge>
        </div>
      </div>
      <h3>{concept.name}</h3>
      <p>{definition}</p>
      <div className="kpi-meta">
        <span>
          <ShieldCheckmarkRegular /> {concept.domain}
        </span>
        <span>Owner · {concept.ownerId.replace('owner-', '').replaceAll('-', ' ')}</span>
      </div>
      <div className="kpi-health">
        <span>Governance state</span>
        <strong>{concept.lifecycle.replaceAll('_', ' ')}</strong>
      </div>
      <div className="kpi-card-footer">
        <span>{concept.aliases.length} aliases</span>
        <span>
          Open record <ChevronRightRegular />
        </span>
      </div>
    </Link>
  );
}

export function CatalogPage() {
  const kpis = useQuery({ queryKey: ['kpis'], queryFn: queries.kpis });
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const filtered = kpis.data?.items.filter((item) =>
    `${item.name} ${item.aliases.join(' ')} ${item.domain}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <div className="page">
      <KpiManagementNav />
      <PageHeader
        eyebrow="KPI Management · trusted definitions"
        title="KPI Catalog"
        description="Find the approved definition, owner, active context, and conforming implementations to use."
      />
      <div className="catalog-toolbar">
        <label className="inline-search">
          <SearchRegular />
          <input
            value={query}
            onChange={(event) => setParams(event.target.value ? { q: event.target.value } : {})}
            placeholder="Search by KPI, alias, owner, or domain"
          />
        </label>
      </div>
      <QueryBoundary query={kpis}>
        {() =>
          filtered?.length ? (
            <section className="kpi-grid">
              {filtered.map((concept) => (
                <KpiCard key={concept.id} concept={concept} />
              ))}
            </section>
          ) : (
            <EmptyState
              title="No KPI matches"
              body="Try a broader name, alias, owner, or domain."
            />
          )
        }
      </QueryBoundary>
    </div>
  );
}

function MeasureClassification({ item }: { item: MeasureView }) {
  if (!item.classification) return <StatusBadge tone="warning">Unclassified</StatusBadge>;
  return (
    <div className="classification-cell">
      <StatusBadge
        tone={
          item.classification.classification === 'CANONICAL_KPI_CANDIDATE'
            ? 'success'
            : item.classification.secondaryTags.includes('PLACEHOLDER_EXPRESSION')
              ? 'warning'
              : 'neutral'
        }
      >
        {item.classification.classification.replaceAll('_', ' ')}
      </StatusBadge>
      <small>{Math.round(item.classification.confidence * 100)}% confidence</small>
    </div>
  );
}

export function MeasuresPage() {
  const [params] = useSearchParams();
  const initialQuery = params.get('q') ?? '';
  const initialClassification = params.get('classification') ?? '';
  const [page, setPage] = useState(1);
  const [draftQuery, setDraftQuery] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [classification, setClassification] = useState(initialClassification);
  const [view, setView] = useState<'inventory' | 'matrix'>('inventory');
  const measures = useQuery({
    queryKey: ['measures', page, query, classification],
    queryFn: () => queries.measures({ page, pageSize: 25, q: query, classification }),
  });
  const matrix = useQuery({
    queryKey: ['measure-matrix'],
    queryFn: queries.matrix,
    enabled: view === 'matrix',
  });
  return (
    <div className="page">
      <GraphExplorerNav />
      <PageHeader
        eyebrow="Graph Explorer · 2D evidence"
        title="Measures"
        description="Inspect every observed measure, its explainable classification, usage evidence, dependencies, and coverage. A KPI candidate is not a governed KPI."
      />
      <div className="catalog-toolbar measures-toolbar panel">
        <form
          className="inline-search"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQuery(draftQuery.trim());
          }}
        >
          <SearchRegular />
          <input
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder="Search measure, table, or semantic model"
          />
        </form>
        <label className="measure-filter">
          <span>Classification</span>
          <select
            value={classification}
            onChange={(event) => {
              setPage(1);
              setClassification(event.target.value);
            }}
          >
            <option value="">All classes</option>
            <option value="CANONICAL_KPI_CANDIDATE">Canonical KPI candidates</option>
            <option value="PRESENTED_METRIC_CANDIDATE">Presented metric candidates</option>
            <option value="KPI_PROJECTION">KPI projections</option>
            <option value="HELPER_OR_COMPONENT_MEASURE">Helpers/components</option>
            <option value="UNRESOLVED_MEASURE">Unresolved</option>
          </select>
        </label>
        <div className="view-switch" aria-label="Measures view">
          <button
            className={view === 'inventory' ? 'active' : ''}
            aria-pressed={view === 'inventory'}
            onClick={() => setView('inventory')}
          >
            <ListRegular /> Inventory
          </button>
          <button
            className={view === 'matrix' ? 'active' : ''}
            aria-pressed={view === 'matrix'}
            onClick={() => setView('matrix')}
          >
            <GridRegular /> Usage matrix
          </button>
        </div>
      </div>
      {view === 'inventory' ? (
        <QueryBoundary query={measures}>
          {(data) =>
            data.items.length ? (
              <section className="panel measure-inventory">
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Measure</th>
                        <th>Classification</th>
                        <th>Visual usage</th>
                        <th>Dependencies</th>
                        <th>Coverage</th>
                        <th>Graph</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.name}</strong>
                            <span className="cell-sub">
                              {item.workspaceName ?? 'Workspace'} ·{' '}
                              {item.modelName ?? item.semanticModelId}
                              {' · '}
                              {item.tableName}
                            </span>
                          </td>
                          <td>
                            <MeasureClassification item={item} />
                          </td>
                          <td>
                            <strong>{item.usageCount ?? 0} visuals</strong>
                            <span className="cell-sub">{item.reportCount ?? 0} reports</span>
                          </td>
                          <td>
                            {(item.inboundDependencyCount ?? 0) +
                              (item.outboundDependencyCount ?? 0)}
                          </td>
                          <td>
                            <CoverageBadge state={item.provenance.coverageState} />
                          </td>
                          <td>
                            <Link className="table-link" to={`/graph?focus=${item.id}`}>
                              Explore
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  total={data.total}
                  onPage={setPage}
                />
              </section>
            ) : (
              <EmptyState
                title="No measures match"
                body="Adjust the search or classification filter, or run a metadata scan."
              />
            )
          }
        </QueryBoundary>
      ) : (
        <QueryBoundary query={matrix}>
          {(data) =>
            data.cells.length ? (
              <section className="panel measure-matrix">
                <SectionTitle title="Measure × dimension matrix" />
                <p className="matrix-description">
                  Cells exist only when a measure and dimension were bound to the same inspected
                  report visual.
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Measure</th>
                        <th>Dimension</th>
                        <th>Reports</th>
                        <th>Visuals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cells.map((cell) => (
                        <tr key={`${cell.measureId}:${cell.dimensionId}`}>
                          <td>
                            <strong>{cell.measureName}</strong>
                          </td>
                          <td>{cell.dimensionName}</td>
                          <td>{cell.reportCount}</td>
                          <td>{cell.visualCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : (
              <EmptyState
                title="No measure–dimension co-usage observed"
                body="The matrix remains empty until report definitions expose both measure and column bindings. This is a coverage statement, not evidence that dimensions are unused."
              />
            )
          }
        </QueryBoundary>
      )}
    </div>
  );
}

export function SearchPage() {
  const [params] = useSearchParams();
  const query = params.get('q') ?? '';
  const results = useQuery({
    queryKey: ['global-search', query],
    queryFn: () => queries.search(query),
    enabled: Boolean(query.trim()),
  });
  return (
    <div className="page">
      <PageHeader
        eyebrow="Enterprise metadata search"
        title={`Results for “${query}”`}
        description="Search spans governed KPIs, observed measures, and reports in the active registry."
      />
      <QueryBoundary query={results}>
        {(data) =>
          data.items.length ? (
            <section className="panel search-results">
              {data.items.map((item) => (
                <Link key={`${item.kind}:${item.id}`} to={item.href}>
                  <StatusBadge>{item.kind}</StatusBadge>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.context}</small>
                  </span>
                  <ChevronRightRegular />
                </Link>
              ))}
            </section>
          ) : (
            <EmptyState
              title="No matching registry records"
              body="Try a KPI name, measure/table name, or report title."
            />
          )
        }
      </QueryBoundary>
    </div>
  );
}

export function KpiRecordPage() {
  const { id = '' } = useParams();
  const record = useQuery({ queryKey: ['kpi', id], queryFn: () => queries.kpi(id) });
  const [tab, setTab] = useState('summary');
  return (
    <div className="page">
      <KpiManagementNav />
      <QueryBoundary query={record}>
        {(data) => {
          const version = data.versions[0];
          const validationScore = data.implementationBindings.length
            ? Math.round(
                (data.implementationBindings.filter(({ validationState }) =>
                  ['PASSED', 'WARNING'].includes(validationState),
                ).length /
                  data.implementationBindings.length) *
                  100,
              )
            : 0;
          const conflictCount = data.implementationBindings.filter(
            ({ conformance }) => conformance === 'CONFLICTING',
          ).length;
          const recordCoverage =
            data.relatedMeasures.length === 0
              ? 'UNKNOWN'
              : data.relatedMeasures.every(
                    ({ provenance }) => provenance.coverageState === 'COMPLETE',
                  )
                ? 'COMPLETE'
                : 'PARTIAL';
          return (
            <>
              <div className="record-heading">
                <Link to="/catalog" className="back-link">
                  ← KPI Catalog
                </Link>
                <div className="record-title-row">
                  <div>
                    <div className="record-badges">
                      <StatusBadge
                        tone={data.concept.lifecycle === 'APPROVED' ? 'success' : 'warning'}
                      >
                        {data.concept.lifecycle}
                      </StatusBadge>
                      <StatusBadge tone="info">{data.concept.criticality}</StatusBadge>
                      <CoverageBadge state={recordCoverage} />
                    </div>
                    <h1>{data.concept.name}</h1>
                    <p>{data.concept.businessQuestion}</p>
                  </div>
                  <div className="page-actions">
                    <Link className="primary-link" to={`/graph?focus=${data.concept.id}`}>
                      Explore relationships <ArrowExportRegular />
                    </Link>
                  </div>
                </div>
              </div>
              <TabList
                selectedValue={tab}
                onTabSelect={(_, value) => setTab(String(value.value))}
                className="record-tabs"
              >
                <Tab value="summary">Summary</Tab>
                <Tab value="business">Business</Tab>
                <Tab value="technical">Technical</Tab>
                <Tab value="implementations">Implementations</Tab>
                <Tab value="variants">Variants & projections</Tab>
                <Tab value="impact">Impact</Tab>
                <Tab value="validation">Validation</Tab>
                <Tab value="history">History</Tab>
              </TabList>
              {tab === 'summary' ? (
                <div className="record-layout">
                  <section className="panel definition-card">
                    <span className="card-kicker">
                      {(version?.lifecycle ?? 'DRAFT')
                        .toLocaleLowerCase()
                        .replace(/^./, (value) => value.toUpperCase())}{' '}
                      business definition · v{version?.version ?? 1}
                    </span>
                    <h2>{version?.definition ?? 'Definition pending approval.'}</h2>
                    <div className="definition-grid">
                      <div>
                        <span>Grain</span>
                        <strong>{version?.grain ?? '—'}</strong>
                      </div>
                      <div>
                        <span>Time basis</span>
                        <strong>{version?.timeBasis ?? '—'}</strong>
                      </div>
                      <div>
                        <span>Unit</span>
                        <strong>{version?.unit ?? '—'}</strong>
                      </div>
                      <div>
                        <span>Effective</span>
                        <strong>{version ? formatDate(version.validFrom) : '—'} → current</strong>
                      </div>
                    </div>
                    <div className="formula-anatomy">
                      <div>
                        <span>Numerator</span>
                        <p>{version?.numerator ?? 'Not separately defined'}</p>
                      </div>
                      <div>
                        <span>Denominator</span>
                        <p>{version?.denominator ?? 'Not applicable'}</p>
                      </div>
                      <div>
                        <span>Known limitations</span>
                        <p>
                          {version?.limitations.join(' · ') || 'No approved limitations recorded.'}
                        </p>
                      </div>
                    </div>
                  </section>
                  <aside className="record-aside">
                    <section className="panel owner-card">
                      <SectionTitle title="Ownership" />
                      <dl>
                        <div>
                          <dt>Business owner</dt>
                          <dd>{data.concept.ownerId}</dd>
                        </div>
                        <div>
                          <dt>Data steward</dt>
                          <dd>{data.concept.stewardId}</dd>
                        </div>
                        <div>
                          <dt>Domain</dt>
                          <dd>{data.concept.domain}</dd>
                        </div>
                      </dl>
                    </section>
                    <section className="panel health-card">
                      <SectionTitle title="Implementation health" />
                      <div className="health-big">
                        <strong>{validationScore}</strong>
                        <span>/ 100</span>
                      </div>
                      <p>
                        {data.implementationBindings.length} observed implementation(s);{' '}
                        {conflictCount} conflicting binding(s).
                      </p>
                      {conflictCount > 0 ? (
                        <Link to="/reviews">
                          Review conflict <ArrowRightRegular />
                        </Link>
                      ) : null}
                    </section>
                  </aside>
                  <section className="panel span-full">
                    <SectionTitle title="Governed family" />
                    <div className="family-row">
                      <div>
                        <b>{data.variants.length}</b>
                        <span>Active variants</span>
                      </div>
                      <div>
                        <b>{data.projections.length}</b>
                        <span>Named projections</span>
                      </div>
                      <div>
                        <b>{data.contextSlices.length}</b>
                        <span>Context slices</span>
                      </div>
                      <div>
                        <b>{data.implementationBindings.length}</b>
                        <span>Observed implementations</span>
                      </div>
                    </div>
                  </section>
                </div>
              ) : (
                <RecordTab tab={tab} data={data} />
              )}
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

function RecordTab({ tab, data }: { tab: string; data: Awaited<ReturnType<typeof queries.kpi>> }) {
  return (
    <section className="panel record-tab-panel">
      <SectionTitle
        title={tab.replaceAll('-', ' ').replace(/^./, (value) => value.toUpperCase())}
      />
      {tab === 'implementations' ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Observed measure</th>
                <th>Model</th>
                <th>Coverage</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {data.relatedMeasures.map((measure) => (
                <tr key={measure.id}>
                  <td>
                    <strong>{measure.name}</strong>
                    <small className="cell-sub">{measure.tableName}</small>
                  </td>
                  <td>{measure.semanticModelId}</td>
                  <td>
                    <CoverageBadge state={measure.provenance.coverageState} />
                  </td>
                  <td>
                    <StatusBadge tone="success">Observed</StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === 'variants' ? (
        <div className="governed-items">
          {data.variants.map((item) => (
            <article key={item.id}>
              <span className="shape diamond">◆</span>
              <div>
                <strong>{item.name}</strong>
                <p>{item.businessJustification}</p>
              </div>
              <StatusBadge tone="violet">{item.type}</StatusBadge>
            </article>
          ))}
          {data.projections.slice(0, 4).map((item) => (
            <article key={item.id}>
              <span className="shape projection">◌</span>
              <div>
                <strong>{item.name}</strong>
                <p>{item.description}</p>
              </div>
              <StatusBadge tone="info">{item.type}</StatusBadge>
            </article>
          ))}
        </div>
      ) : (
        <div className="prose-panel">
          <p>
            The active governed record keeps business meaning, technical evidence, effective time,
            and approval history separate. This {tab} view is backed by the same temporal contract
            and audit model.
          </p>
          <ExplanationPath
            items={[
              data.concept.name,
              'Approved contract version',
              `${data.relatedMeasures.length} observed implementations`,
              'Evidence-backed report usage',
            ]}
          />
        </div>
      )}
    </section>
  );
}

const triageColumns: Array<{
  key: ReviewCaseView['triageType'];
  title: string;
  description: string;
  nextAction: string;
  tone: 'success' | 'danger' | 'warning' | 'neutral';
}> = [
  {
    key: 'GOVERNED',
    title: 'Governed KPI',
    description: 'Approved KPI implementations bound to current contract versions.',
    nextAction: 'audit conformance and endorsement drift',
    tone: 'success',
  },
  {
    key: 'CANDIDATE',
    title: 'KPI candidates',
    description: 'Presented and canonical KPI candidates awaiting owner validation.',
    nextAction: 'steward validation and business-owner review',
    tone: 'neutral',
  },
  {
    key: 'CONFLICTING',
    title: 'Conflicting',
    description: 'Same KPI name or business claim with materially different DAX.',
    nextAction: 'select the governed business rule',
    tone: 'danger',
  },
  {
    key: 'DUPLICATE',
    title: 'Duplicate / replicated',
    description: 'Formula copies and renamed replicas that need alias or ownership decisions.',
    nextAction: 'confirm alias, owner, or deprecation',
    tone: 'warning',
  },
  {
    key: 'PLACEHOLDER',
    title: 'Placeholder',
    description: 'Zero, BLANK(), string, or scaffold measures kept out of duplicate buckets.',
    nextAction: 'replace scaffold formula before KPI review',
    tone: 'neutral',
  },
];

function daxReferences(expression: string | undefined): string[] {
  return [...new Set(expression?.match(/\[[^\]]+\]/g) ?? [])].slice(0, 8);
}

function dimensionReferences(expression: string | undefined): string[] {
  return [
    ...new Set(
      expression?.match(/[A-Za-z0-9_ ]+\[[A-Za-z0-9_ ]+\]/g)?.filter((reference) => {
        const table = reference.split('[')[0]?.trim();
        return Boolean(table && table !== '');
      }) ?? [],
    ),
  ].slice(0, 8);
}

function interpretDax(expression: string | undefined): string {
  const dax = expression?.trim() ?? '';
  if (!dax) return 'No formula was captured for this observed measure.';
  if (/^(0|1|-?\d+(\.\d+)?|BLANK\(\)|".*")$/i.test(dax))
    return 'This is a trivial constant or placeholder expression; it should not be treated as a real KPI implementation until the owner replaces it.';
  if (/^DIVIDE\(/i.test(dax))
    return 'This divides one business quantity by another, usually producing a rate, margin, conversion, or attainment metric.';
  if (/DATESYTD|DATESMTD|DATESQTD|SAMEPERIODLASTYEAR|PARALLELPERIOD/i.test(dax))
    return 'This is a time projection or comparison over another measure, not usually a separate canonical KPI identity.';
  if (/^CALCULATE\(/i.test(dax))
    return 'This changes filter context around a base expression, so reviewers should verify whether it is a governed variant, context slice, or projection.';
  if (/SUMX|AVERAGEX|COUNTX/i.test(dax))
    return 'This iterates rows to derive a result; reviewers should check row grain, filters, and whether the row logic matches the business definition.';
  if (/^SUM\(/i.test(dax))
    return 'This aggregates a column directly and is likely a base metric or component measure.';
  return 'MetricGraph captured the expression, but this first-pass interpreter needs steward review for the exact business meaning.';
}

function usageSummary(item: ReviewCaseView) {
  const reports = [
    ...new Set([...(item.leftUsage?.reports ?? []), ...(item.rightUsage?.reports ?? [])]),
  ].slice(0, 8);
  const workspaces = [
    ...new Set([...(item.leftUsage?.workspaces ?? []), ...(item.rightUsage?.workspaces ?? [])]),
  ].slice(0, 5);
  return {
    reportCount: (item.leftUsage?.reportCount ?? 0) + (item.rightUsage?.reportCount ?? 0),
    visualCount: (item.leftUsage?.visualCount ?? 0) + (item.rightUsage?.visualCount ?? 0),
    viewCount: (item.leftUsage?.viewCount ?? 0) + (item.rightUsage?.viewCount ?? 0),
    viewerCount: (item.leftUsage?.viewerCount ?? 0) + (item.rightUsage?.viewerCount ?? 0),
    reports,
    workspaces,
  };
}

function boundedDepth(value: string | null, fallback = 2) {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(3, Math.max(1, Math.trunc(parsed)));
}

function graphLink(
  focusId: string,
  lens: string,
  depth: number,
  direction: 'UPSTREAM' | 'DOWNSTREAM' | 'BOTH',
) {
  return `/graph?focus=${encodeURIComponent(focusId)}&lens=${lens}&depth=${depth}&direction=${direction}`;
}

export function ReviewPage() {
  const [page, setPage] = useState(1);
  const [triage, setTriage] = useState<ReviewCaseView['triageType']>('CANDIDATE');
  const board = useQueries({
    queries: triageColumns.map((column) => ({
      queryKey: ['reviews', 'board', column.key],
      queryFn: () => queries.reviews({ page: 1, pageSize: 3, triage: column.key }),
    })),
  });
  const reviews = useQuery({
    queryKey: ['reviews', page, triage],
    queryFn: () => queries.reviews({ page, pageSize: 25, triage }),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    reviews.data?.items.find((item) => item.id === selectedId) ?? reviews.data?.items[0];
  return (
    <div className="page review-page">
      <KpiManagementNav />
      <PageHeader
        eyebrow="KPI governance operations"
        title="Work Queue"
        description="Kanban triage for KPI candidates, conflicting definitions, replicated measures, placeholders, approvals, endorsement, and impact review."
      />
      <section className="kanban-summary panel" aria-labelledby="kanban-title">
        <div>
          <span className="card-kicker">Operating model</span>
          <h2 id="kanban-title">KPI stage kanban</h2>
          <p>
            Stage load, next governance action, and live examples from each queue. Select a lane to
            inspect DAX, impact, approval, endorsement, and draft-promotion evidence.
          </p>
        </div>
      </section>
      <div className="triage-board" role="tablist" aria-label="KPI management stages">
        {triageColumns.map((column, index) => (
          <button
            key={column.key}
            role="tab"
            aria-selected={triage === column.key}
            aria-label={
              column.key === 'DUPLICATE'
                ? 'duplicate'
                : column.key === 'CANDIDATE'
                  ? 'candidate'
                  : column.key === 'GOVERNED'
                    ? 'governed'
                    : column.key.toLocaleLowerCase()
            }
            className={`${triage === column.key ? 'active' : ''} ${column.tone}`}
            onClick={() => {
              setSelectedId(null);
              setPage(1);
              setTriage(column.key);
            }}
          >
            <span>{column.title}</span>
            <strong>{board[index]?.data?.total?.toLocaleString() ?? '…'}</strong>
            <small>{column.description}</small>
            <em>Next: {column.nextAction}</em>
            <div className="stage-preview-list" aria-label={`${column.title} case preview`}>
              {(board[index]?.data?.items ?? []).map((item) => (
                <i key={item.id}>
                  {item.left?.name ?? item.leftMeasureId}
                  <b>{item.decision}</b>
                </i>
              ))}
              {board[index]?.data?.items.length === 0 ? <i>No current cases</i> : null}
            </div>
          </button>
        ))}
      </div>
      <QueryBoundary query={reviews}>
        {(data) =>
          data.items.length ? (
            <div className="review-layout">
              <section className="panel review-list">
                <div className="review-list-header">
                  <strong>
                    {data.total.toLocaleString()} {triage.toLocaleLowerCase()} cases
                  </strong>
                </div>
                {data.items.map((item) => (
                  <button
                    key={item.id}
                    className={`review-case ${selected?.id === item.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <span
                      className={`case-mark ${
                        item.triageType === 'CANDIDATE'
                          ? 'success'
                          : item.triageType === 'GOVERNED'
                            ? 'success'
                            : item.type.includes('DIFFERENT_FORMULA')
                              ? 'danger'
                              : 'warning'
                      }`}
                    >
                      <WarningRegular />
                    </span>
                    <span>
                      <strong>
                        {item.right
                          ? `${item.left?.name} ↔ ${item.right.name}`
                          : (item.left?.name ?? 'Observed measure')}
                      </strong>
                      <small>{item.type.replaceAll('_', ' ').toLowerCase()}</small>
                      <em>{Math.round(item.score * 100)}% evidence score</em>
                    </span>
                    <ChevronRightRegular />
                  </button>
                ))}
                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  total={data.total}
                  onPage={(next) => {
                    setSelectedId(null);
                    setPage(next);
                  }}
                />
              </section>
              {selected ? <ReviewWorkbench item={selected} /> : null}
            </div>
          ) : (
            <EmptyState
              title="This KPI management stage is clear"
              body="New discovery and governance exceptions will appear here."
            />
          )
        }
      </QueryBoundary>
    </div>
  );
}

function ReviewWorkbench({ item }: { item: ReviewCaseView }) {
  const client = useQueryClient();
  const relatedUsage = usageSummary(item);
  const governance = item.governanceContext;
  const reviewPacket = item.reviewPacket;
  const focusMeasureId = item.left?.id ?? item.leftMeasureId;
  const measureSearch = encodeURIComponent(item.left?.name ?? item.leftMeasureId);
  const impactConfidence =
    reviewPacket?.impactSimulation.confidence ??
    (relatedUsage.visualCount > 0
      ? `${Math.min(100, Math.round(item.score * 100))}% evidence confidence`
      : 'Coverage gap: no visual usage observed');
  const impactedScope = reviewPacket
    ? `${reviewPacket.impactSimulation.reportCount} report(s) across ${Math.max(1, relatedUsage.workspaces.length)} workspace(s)`
    : relatedUsage.reportCount > 0
      ? `${relatedUsage.reportCount} report(s) across ${Math.max(1, relatedUsage.workspaces.length)} workspace(s)`
      : 'No downstream report usage captured in the current scan coverage';
  const governanceFacts: Array<[string, string]> = governance
    ? (
        [
          ['Domain', governance.domain],
          ['Lifecycle', governance.lifecycle],
          ['Criticality', governance.criticality],
          ['Contract', governance.contractVersionId],
          ['Conformance', governance.conformance],
          ['Validation', governance.validationState],
          ['Semantic model endorsement', governance.semanticModelEndorsement],
        ] satisfies Array<[string, string | null]>
      ).flatMap(([label, value]) => (value ? [[label, value]] : []))
    : [];
  const reportEndorsements = governance?.reportEndorsements.length
    ? governance.reportEndorsements
    : ['No report endorsement captured'];
  const governedVariants = governance?.variants.length
    ? governance.variants
    : ['No governed variants recorded'];
  const governedProjections = governance?.projections.length
    ? governance.projections
    : ['No governed projections recorded'];
  const referencedMeasures = [
    ...new Set([...daxReferences(item.left?.expression), ...daxReferences(item.right?.expression)]),
  ];
  const referencedDimensions = [
    ...new Set([
      ...dimensionReferences(item.left?.expression),
      ...dimensionReferences(item.right?.expression),
    ]),
  ];
  const proposedBusinessDescription =
    reviewPacket?.proposedBusinessDescription ??
    (item.triageType === 'GOVERNED'
      ? `${item.left?.name ?? 'This measure'} is already bound to a governed KPI contract. Use this lane to audit conformance, validation, endorsement, and downstream impact.`
      : item.triageType === 'CANDIDATE'
        ? `Validate whether ${item.left?.name ?? 'this measure'} should become a governed KPI, a presented metric, a projection, or remain an unmanaged report measure.`
        : item.triageType === 'CONFLICTING'
          ? `Resolve the business definition for ${item.left?.name ?? 'this metric'} and bind only the conforming measure to the governed KPI contract.`
          : item.triageType === 'DUPLICATE'
            ? `Treat ${item.right?.name ?? 'the replicated measure'} as an alias or deprecate it after downstream report owners confirm no business-rule difference.`
            : `Replace ${item.left?.name ?? 'the placeholder'} with an owner-approved business definition before it can become a KPI candidate.`);
  const proposedFormula =
    reviewPacket?.proposedFormula ??
    (item.triageType === 'PLACEHOLDER'
      ? `-- Steward draft required\nCALCULATE([Approved Base Metric], 'Date'[Date] <= MAX('Date'[Date]))`
      : (item.left?.expression ?? item.right?.expression ?? '-- No captured expression'));
  const previewBase =
    reviewPacket?.impactSimulation.baselineValue ?? 1_000_000 + Math.round(item.score * 250_000);
  const previewDelta =
    reviewPacket?.impactSimulation.deltaPercent ??
    (item.triageType === 'CONFLICTING' ? -8.7 : item.triageType === 'DUPLICATE' ? 0 : 100);
  const previewProposed =
    reviewPacket?.impactSimulation.proposedValue ??
    Math.round(previewBase * (1 + previewDelta / 100));
  const impactWorkspaces = reviewPacket?.impactWorkspaces ?? [];
  const notificationDraft =
    reviewPacket?.notificationDraft ??
    `Review proposed MetricGraph change for ${item.left?.name ?? 'this measure'}. No downstream visual usage was observed in the current scan coverage; please validate manually before any semantic model update or Power BI endorsement request.`;
  const approvalSteps = reviewPacket?.approvalSteps ?? [
    item.triageType === 'GOVERNED'
      ? 'KPI approval is already represented by the active contract binding.'
      : 'Steward validates DAX, source lineage, and visual-usage evidence.',
    'Business owner approves canonical KPI, alias, conflict, or placeholder disposition.',
  ];
  const endorsementSteps = reviewPacket?.endorsementSteps ?? [
    'Certified endorsement remains separate from KPI approval and requires authorized review.',
    'Impact notice goes to affected workspace contacts before semantic model changes.',
  ];
  const [reason, setReason] = useState(
    'Reviewed the source expression, classification, coverage, and available dependency evidence.',
  );
  const decision = useMutation({
    mutationFn: (verdict: 'APPROVED' | 'REJECTED') =>
      queries.decideReview(item.id, reason, verdict),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['reviews'] }),
  });
  const draftPromotion = useMutation({
    mutationFn: () => queries.promoteCandidateDraft(item.id, reason),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['reviews'] }),
  });
  return (
    <section className="panel workbench">
      <div className="workbench-head">
        <div>
          <span className="card-kicker">{item.type.replaceAll('_', ' ')}</span>
          <h2>
            {item.triageType === 'GOVERNED'
              ? 'Inspect governed KPI implementation'
              : item.triageType === 'CANDIDATE'
                ? 'Validate KPI candidate and endorsement readiness'
                : item.triageType === 'PLACEHOLDER'
                  ? 'Triage placeholder before it pollutes reconciliation'
                  : 'Compare the claimed KPI identity'}
          </h2>
          <p>{item.reasons.join(' · ')}</p>
        </div>
        <StatusBadge tone={item.decision === 'PENDING' ? 'warning' : 'success'}>
          {item.decision}
        </StatusBadge>
      </div>
      <div className={`compare-grid ${item.right ? '' : 'single'}`}>
        <article>
          <span>Implementation A</span>
          <h3>
            {item.left?.tableName}.[{item.left?.name}]
          </h3>
          <code>{item.left?.expression}</code>
          <dl>
            <div>
              <dt>Model</dt>
              <dd>{item.left?.semanticModelId}</dd>
            </div>
            <div>
              <dt>Coverage</dt>
              <dd>{item.left?.provenance.coverageState}</dd>
            </div>
          </dl>
        </article>
        {item.right ? (
          <article>
            <span>Implementation B</span>
            <h3>
              {item.right.tableName}.[{item.right.name}]
            </h3>
            <code>{item.right.expression}</code>
            <dl>
              <div>
                <dt>Model</dt>
                <dd>{item.right.semanticModelId}</dd>
              </div>
              <div>
                <dt>Coverage</dt>
                <dd>{item.right.provenance.coverageState}</dd>
              </div>
            </dl>
          </article>
        ) : null}
      </div>
      <div className="management-grid">
        <article>
          <span className="card-kicker">Formula interpretation</span>
          <p>{interpretDax(item.left?.expression)}</p>
          {item.right ? <p>{interpretDax(item.right.expression)}</p> : null}
          <div className="evidence-chips">
            {(referencedMeasures.length
              ? referencedMeasures
              : ['No measure references captured']
            ).map((reference) => (
              <span key={reference}>{reference}</span>
            ))}
          </div>
        </article>
        <article>
          <span className="card-kicker">Relevant dimensions</span>
          <div className="evidence-chips">
            {(referencedDimensions.length
              ? referencedDimensions
              : ['No formula-level dimensions captured']
            ).map((reference) => (
              <span key={reference}>{reference}</span>
            ))}
          </div>
          <p>
            Matrix usage is captured from visual field bindings where report definition coverage is
            available.
          </p>
        </article>
        <article>
          <span className="card-kicker">Reports and workspaces</span>
          <strong>
            {relatedUsage.reportCount} report(s) · {relatedUsage.visualCount} visual observation(s)
          </strong>
          <p>
            Last-30-day synthetic impact: {relatedUsage.viewerCount.toLocaleString()} viewer(s),{' '}
            {relatedUsage.viewCount.toLocaleString()} view(s). Notify affected workspace contacts
            before changing a shared semantic model.
          </p>
          <div className="evidence-chips">
            {(relatedUsage.reports.length
              ? relatedUsage.reports
              : ['No visual usage observed']
            ).map((report) => (
              <span key={report}>{report}</span>
            ))}
          </div>
          <small>{relatedUsage.workspaces.join(' · ') || 'No workspace usage observed'}</small>
          <div className="evidence-actions" aria-label="Evidence navigation">
            <Link
              className="button-link primary"
              to={graphLink(focusMeasureId, 'USAGE_IMPACT', 3, 'UPSTREAM')}
            >
              Open impact graph
            </Link>
            <Link className="button-link" to={`/measures?q=${measureSearch}`}>
              Open measure evidence
            </Link>
            {governance?.kpiId ? (
              <Link className="button-link" to={`/catalog/${encodeURIComponent(governance.kpiId)}`}>
                Open governed KPI
              </Link>
            ) : null}
          </div>
        </article>
        <article>
          <span className="card-kicker">Governed KPI context</span>
          {governance && governance.source !== 'UNMAPPED' ? (
            <>
              <p>
                {governance.source === 'BINDING'
                  ? `${governance.kpiName ?? 'This KPI'} is backed by an approved implementation binding.`
                  : `${item.left?.name ?? 'This measure'} matches a governed KPI name or alias, but still needs an explicit implementation binding.`}
              </p>
              {governance.contractDefinition ? (
                <p>
                  <strong>Contract definition:</strong> {governance.contractDefinition}
                </p>
              ) : null}
              <dl className="governance-facts">
                {governanceFacts.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="evidence-chips">
                {reportEndorsements.map((endorsement) => (
                  <span key={endorsement}>{endorsement}</span>
                ))}
              </div>
              <div className="evidence-chips">
                {[...governedVariants, ...governedProjections].map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </div>
            </>
          ) : (
            <p>
              No governed binding is recorded yet; candidate promotion requires steward review,
              business-owner approval, and a separate endorsement decision.
            </p>
          )}
        </article>
        <article>
          <span className="card-kicker">Proposed update</span>
          <p>{proposedBusinessDescription}</p>
          <code>{proposedFormula}</code>
          <small>
            {reviewPacket?.formulaRationale ??
              'Generated from captured DAX and current review evidence; steward approval is required.'}
          </small>
        </article>
        <article>
          <span className="card-kicker">Approval and endorsement path</span>
          <ol className="process-list">
            {approvalSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <SectionTitle title="Fabric endorsement boundary" />
          <ol className="process-list">
            {endorsementSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </article>
        <article>
          <span className="card-kicker">Impact preview and simulation</span>
          <p>
            {impactedScope}. {impactConfidence}. Preview uses captured usage observations and should
            be revalidated before a shared semantic model change.
          </p>
          <div className="simulation-card">
            <div>
              <small>Current observed value</small>
              <strong>{previewBase.toLocaleString()}</strong>
            </div>
            <div>
              <small>Proposed effect</small>
              <strong>
                {previewDelta > 0 ? '+' : ''}
                {previewDelta}%
              </strong>
            </div>
            <div>
              <small>Proposed value</small>
              <strong>{previewProposed.toLocaleString()}</strong>
            </div>
            <div>
              <small>Impacted viewers</small>
              <strong>
                {(
                  reviewPacket?.impactSimulation.viewerCount ?? relatedUsage.viewerCount
                ).toLocaleString()}
              </strong>
            </div>
            <div>
              <small>Impacted views</small>
              <strong>
                {(
                  reviewPacket?.impactSimulation.viewCount ?? relatedUsage.viewCount
                ).toLocaleString()}
              </strong>
            </div>
          </div>
          <div className="mini-bars" aria-label="Current and proposed metric value preview">
            <span style={{ width: '74%' }} />
            <span style={{ width: `${Math.max(18, Math.min(92, 74 + previewDelta))}%` }} />
          </div>
        </article>
        <article className="wide-card">
          <span className="card-kicker">Workspace impact analysis</span>
          <p>
            Modeled after Power BI semantic model impact analysis: impacted workspaces, reports,
            viewers, views, and the message to send before changing a shared semantic model.
          </p>
          {impactWorkspaces.length ? (
            <div className="impact-workspace-list">
              {impactWorkspaces.map((workspace) => (
                <div key={workspace.workspaceName} className="impact-workspace-card">
                  <div>
                    <strong>{workspace.workspaceName}</strong>
                    <small>{workspace.reports.join(' · ')}</small>
                  </div>
                  <dl>
                    <div>
                      <dt>Reports</dt>
                      <dd>{workspace.reportCount}</dd>
                    </div>
                    <div>
                      <dt>Visuals</dt>
                      <dd>{workspace.visualCount}</dd>
                    </div>
                    <div>
                      <dt>Viewers</dt>
                      <dd>{workspace.viewerCount.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt>Views</dt>
                      <dd>{workspace.viewCount.toLocaleString()}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <div className="notice warning">
              <WarningRegular />
              <span>
                No downstream report usage was observed in the current scan coverage. Treat this as
                incomplete evidence, not proof of zero impact.
              </span>
            </div>
          )}
          <SectionTitle title="Notify contacts draft" />
          <blockquote className="notification-draft">{notificationDraft}</blockquote>
        </article>
      </div>
      <div className="evidence-strip">
        <InfoRegular />
        <div>
          <strong>Why MetricGraph raised this case</strong>
          <p>
            {item.reasons.join('. ')}. Classification remains reviewable and does not grant KPI
            approval.
          </p>
        </div>
      </div>
      <label className="field">
        <span>Decision rationale</span>
        <Textarea
          value={reason}
          onChange={(_, value) => setReason(value.value)}
          resize="vertical"
        />
      </label>
      <div className="workbench-actions">
        {item.right ? (
          <Button
            disabled={decision.isPending || item.decision !== 'PENDING'}
            onClick={() => decision.mutate('REJECTED')}
          >
            Mark unrelated
          </Button>
        ) : null}
        <Button
          appearance="primary"
          disabled={
            decision.isPending || item.decision !== 'PENDING' || item.triageType === 'GOVERNED'
          }
          onClick={() => decision.mutate('APPROVED')}
        >
          {decision.isPending
            ? 'Recording…'
            : item.triageType === 'GOVERNED'
              ? 'Governed record'
              : item.decision !== 'PENDING'
                ? 'Decision recorded'
                : item.triageType === 'PLACEHOLDER'
                  ? 'Acknowledge placeholder'
                  : item.triageType === 'CANDIDATE'
                    ? 'Start endorsement review'
                    : 'Confirm exact implementation'}
        </Button>
        {item.triageType === 'CANDIDATE' ? (
          <Button
            disabled={draftPromotion.isPending || item.decision === 'PROMOTED_TO_DRAFT'}
            onClick={() => draftPromotion.mutate()}
          >
            {draftPromotion.isPending
              ? 'Creating draft…'
              : item.decision === 'PROMOTED_TO_DRAFT'
                ? 'Draft created'
                : 'Create governed draft'}
          </Button>
        ) : null}
      </div>
      {decision.isSuccess ? (
        <div className="notice success">
          <CheckmarkCircleRegular />
          <span>Decision recorded with actor, rationale, and audit timestamp.</span>
        </div>
      ) : null}
      {draftPromotion.isSuccess ? (
        <div className="notice success">
          <CheckmarkCircleRegular />
          <span>
            Governed draft created from review packet. Approval and Fabric endorsement were not
            inherited.
          </span>
        </div>
      ) : null}
    </section>
  );
}

export function GraphPage() {
  const [params] = useSearchParams();
  const initialFocus = params.get('focus') ?? 'graph-enterprise-estate';
  const initialLens = [
    'BUSINESS',
    'DEPENDENCIES',
    'USAGE_IMPACT',
    'RECONCILIATION',
    'SANDBOX',
  ].includes(params.get('lens') ?? '')
    ? (params.get('lens') ?? 'BUSINESS')
    : 'BUSINESS';
  const initialDirection = ['UPSTREAM', 'DOWNSTREAM', 'BOTH'].includes(
    params.get('direction') ?? '',
  )
    ? (params.get('direction') as 'UPSTREAM' | 'DOWNSTREAM' | 'BOTH')
    : 'BOTH';
  const [lens, setLens] = useState(initialLens);
  const [depth, setDepth] = useState(boundedDepth(params.get('depth'), 2));
  const [direction, setDirection] = useState<'UPSTREAM' | 'DOWNSTREAM' | 'BOTH'>(initialDirection);
  const [focusId, setFocusId] = useState(initialFocus);
  const [selectedId, setSelectedId] = useState(initialFocus);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceContainerSelection | null>(
    null,
  );
  const [rendererKey, setRendererKey] = useState(0);
  const graph = useQuery({
    queryKey: ['graph', focusId, lens, depth, direction],
    queryFn: () => queries.graph(focusId, lens, depth, direction),
  });
  const selected = graph.data?.nodes.find((node) => node.id === selectedId) ?? graph.data?.nodes[0];
  const lensDescriptions: Record<string, string> = {
    BUSINESS:
      'Enterprise estate, governed KPI families, data-product purposes, reports, and primary implementations.',
    DEPENDENCIES: 'Formula components and directional technical dependencies.',
    USAGE_IMPACT: 'Reports and consumers that may be affected by a definition change.',
    RECONCILIATION: 'Similar, duplicate, and materially conflicting implementations.',
    SANDBOX: 'Temporary definitions, derivation, expiry, and promotion paths.',
  };
  return (
    <div className="page graph-page">
      <GraphExplorerNav />
      <PageHeader
        eyebrow="Enterprise relationship intelligence"
        title="Graph Explorer"
        description="See all observed measures as an enterprise map: KPI candidates in green, conflicts in red, duplicates in orange, reports in blue, and workspaces as 3D grouping nodes."
      />
      <section className="graph-toolbar panel">
        <div className="lens-tabs" role="tablist" aria-label="Graph lens">
          {Object.keys(lensDescriptions).map((value) => (
            <button
              key={value}
              role="tab"
              aria-selected={lens === value}
              className={lens === value ? 'active' : ''}
              onClick={() => setLens(value)}
            >
              {value.replaceAll('_', ' ')}
            </button>
          ))}
        </div>
        <div className="graph-actions">
          <label>
            Depth{' '}
            <select value={depth} onChange={(event) => setDepth(Number(event.target.value))}>
              <option value="1">1 hop</option>
              <option value="2">2 hops</option>
              <option value="3">3 hops</option>
            </select>
          </label>
        </div>
      </section>
      <div className="graph-caption">
        <InfoRegular />
        <span>
          <strong>{lens.replaceAll('_', ' ')} lens.</strong> {lensDescriptions[lens]}
        </span>
      </div>
      <div className="graph-command-bar" aria-label="Graph navigation commands">
        <Button size="small" onClick={() => setRendererKey((value) => value + 1)}>
          Fit graph
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFocusId(initialFocus);
            setSelectedId(initialFocus);
            setSelectedWorkspace(null);
            setDirection('BOTH');
            setRendererKey((value) => value + 1);
          }}
        >
          Reset view
        </Button>
        <Button
          size="small"
          disabled={!selected}
          onClick={() => {
            if (!selected) return;
            setFocusId(selected.sourceRecordId);
            setSelectedId(selected.id);
          }}
        >
          Focus selection
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (selected) setFocusId(selected.sourceRecordId);
            setLens('DEPENDENCIES');
            setDepth(3);
            setDirection('DOWNSTREAM');
          }}
        >
          Expand upstream
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (selected) setFocusId(selected.sourceRecordId);
            setLens('USAGE_IMPACT');
            setDepth(3);
            setDirection('UPSTREAM');
          }}
        >
          Expand downstream
        </Button>
        <Button
          size="small"
          disabled={!selectedId && !selectedWorkspace}
          onClick={() => {
            setSelectedId('');
            setSelectedWorkspace(null);
          }}
        >
          Clear selection
        </Button>
      </div>
      <QueryBoundary query={graph}>
        {(model) => (
          <>
            <div className="graph-layout">
              <section className="graph-stage panel">
                <div className="canvas-hud">
                  <span>
                    <span className="pulse-dot" /> Live 3D force layout
                  </span>
                  <span>
                    {model.nodes.length} nodes · {model.edges.length} relationships
                  </span>
                </div>
                <Suspense fallback={<LoadingState label="Loading graph renderer" />}>
                  <GraphCanvas
                    key={rendererKey}
                    model={model}
                    selectedId={selectedWorkspace ? '' : (selected?.id ?? selectedId)}
                    selectedWorkspaceKey={selectedWorkspace?.key ?? null}
                    onSelect={(id) => {
                      setSelectedWorkspace(null);
                      setSelectedId(id);
                    }}
                    onSelectWorkspace={(workspace) => {
                      setSelectedWorkspace(workspace);
                      setSelectedId('');
                    }}
                  />
                </Suspense>
                <div className="graph-legend">
                  {[
                    ['#45df76', 'KPI / candidate'],
                    ['#ff5f72', 'Conflict'],
                    ['#ffb454', 'Duplicate / replica'],
                    ['#aa83ff', 'Variant'],
                    ['#5db8ff', 'Projection'],
                    ['#75a9bd', 'Measure'],
                    ['#3f9cff', 'Report'],
                    ['#8dc8ff', 'Workspace'],
                  ].map(([color, label]) => (
                    <span key={label}>
                      <b style={{ background: color }} />
                      {label}
                    </span>
                  ))}
                </div>
              </section>
              {selectedWorkspace ? (
                <aside className="panel inspector">
                  <span className="card-kicker">Selected · Workspace container</span>
                  <h2>{selectedWorkspace.name}</h2>
                  <div className="record-badges">
                    <StatusBadge
                      tone={selectedWorkspace.orphanedReports > 0 ? 'danger' : 'success'}
                    >
                      {selectedWorkspace.governanceStatus}
                    </StatusBadge>
                    <CoverageBadge state={model.coverage.state} />
                  </div>
                  <p className="inspector-definition">
                    Workspace is rendered as a 3D grouping node connected to its reports. This panel
                    summarizes report ownership, endorsement posture, lineage, and domain-purpose
                    routing for enterprise governance review.
                  </p>
                  <dl className="inspector-facts">
                    <div>
                      <dt>workspace id</dt>
                      <dd>{selectedWorkspace.workspaceId}</dd>
                    </div>
                    <div>
                      <dt>reports</dt>
                      <dd>{selectedWorkspace.reportCount}</dd>
                    </div>
                    <div>
                      <dt>certified reports</dt>
                      <dd>{selectedWorkspace.certifiedReports}</dd>
                    </div>
                    <div>
                      <dt>promoted reports</dt>
                      <dd>{selectedWorkspace.promotedReports}</dd>
                    </div>
                    <div>
                      <dt>unendorsed reports</dt>
                      <dd>{selectedWorkspace.unendorsedReports}</dd>
                    </div>
                    <div>
                      <dt>orphaned reports</dt>
                      <dd>{selectedWorkspace.orphanedReports}</dd>
                    </div>
                    <div>
                      <dt>metadata coverage</dt>
                      <dd>
                        {selectedWorkspace.completeCoverageReports}/{selectedWorkspace.reportCount}{' '}
                        complete
                      </dd>
                    </div>
                    <div>
                      <dt>data products</dt>
                      <dd>
                        {selectedWorkspace.purposes.length
                          ? selectedWorkspace.purposes.join(', ')
                          : 'No data-product purpose edge captured'}
                      </dd>
                    </div>
                    <div>
                      <dt>semantic models</dt>
                      <dd>{selectedWorkspace.semanticModels.slice(0, 5).join(', ')}</dd>
                    </div>
                  </dl>
                  <SectionTitle title="Governance audit signals" />
                  <ExplanationPath items={selectedWorkspace.auditSignals.slice(0, 4)} />
                  <div className="inspector-actions">
                    <Button
                      onClick={() => {
                        setLens('USAGE_IMPACT');
                        setDepth(3);
                        setDirection('UPSTREAM');
                      }}
                    >
                      Show workspace impact
                    </Button>
                  </div>
                </aside>
              ) : selected ? (
                <aside className="panel inspector">
                  <span className="card-kicker">Selected · {selected.kind}</span>
                  <h2>{selected.label}</h2>
                  <div className="record-badges">
                    <StatusBadge tone={selected.kind === 'SandboxMetric' ? 'violet' : 'success'}>
                      {propertyText(
                        selected.properties.status ?? selected.properties.conformance ?? 'Observed',
                      )}
                    </StatusBadge>
                    <CoverageBadge state={model.coverage.state} />
                  </div>
                  <p className="inspector-definition">
                    {selected.kind === 'KPIConcept'
                      ? 'Recognized sales after returns, discounts, and approved allowances.'
                      : 'Evidence-backed relationship in the active MetricGraph scope.'}
                  </p>
                  <dl className="inspector-facts">
                    {Object.entries(selected.properties).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key.replaceAll('_', ' ')}</dt>
                        <dd>{propertyText(value)}</dd>
                      </div>
                    ))}
                  </dl>
                  <SectionTitle title="Explanation" />
                  <ExplanationPath
                    items={(
                      model.explanationPaths[0]?.narrative.split(' → ') ?? [
                        selected.label,
                        'No further path in this lens',
                      ]
                    ).slice(0, 4)}
                  />
                  <div className="inspector-actions">
                    {selected.kind === 'ObservedMeasure' ? (
                      <Link
                        className="button-link primary"
                        to={`/measures?focus=${selected.sourceRecordId}`}
                      >
                        Open measure
                      </Link>
                    ) : selected.kind === 'KPIConcept' ? (
                      <Link
                        className="button-link primary"
                        to={`/catalog/${selected.sourceRecordId}`}
                      >
                        Open KPI
                      </Link>
                    ) : null}
                    <Button
                      onClick={() => {
                        setFocusId(selected.sourceRecordId);
                        setLens('USAGE_IMPACT');
                        setDepth(3);
                        setDirection('UPSTREAM');
                      }}
                    >
                      Show impact
                    </Button>
                  </div>
                </aside>
              ) : null}
            </div>
            <div className="coverage-footer">
              <CoverageBadge state={model.coverage.state} />
              <span>Current within scope · {model.coverage.nodeCount} projected nodes</span>
              {model.coverage.limitations.length ? (
                <span>
                  <WarningRegular /> {model.coverage.limitations.join(' · ')}
                </span>
              ) : null}
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

export function SandboxPage() {
  const sandbox = useQuery({ queryKey: ['sandbox'], queryFn: queries.sandbox });
  const client = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const promote = useMutation({
    mutationFn: (id: string) => {
      const metric = sandbox.data?.items.find((item) => item.id === id);
      return queries.promoteSandbox(
        id,
        metric?.name ?? 'Promoted sandbox metric',
        'Evidence reviewed; create a governed draft without inherited approval.',
      );
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['sandbox'] }),
  });
  const close = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'archive' | 'dispose' }) =>
      queries.closeSandbox(
        id,
        action,
        action === 'archive'
          ? 'Experiment concluded; preserve evidence for governance history.'
          : 'Experiment is obsolete; dispose it under the recorded disposal rule.',
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['sandbox'] }),
  });
  return (
    <div className="page">
      <KpiManagementNav />
      <PageHeader
        eyebrow="KPI Management · controlled experimentation"
        title="Sandbox"
        description="Test temporary definitions without confusing experimental metrics with trusted KPI contracts."
      />
      <div className="sandbox-banner">
        <BeakerRegular />
        <div>
          <strong>Sandbox trust boundary</strong>
          <span>
            Nothing here is approved for production use. Promotion creates a governed draft and
            always requires review.
          </span>
        </div>
        <LockClosedRegular />
      </div>
      <QueryBoundary query={sandbox}>
        {(data) => (
          <>
            <section className="metric-grid compact">
              <MetricCard
                label="Active experiments"
                value={data.items.filter((item) => item.expiryState === 'ACTIVE').length}
                meta="Within approved sandbox period"
              />
              <MetricCard
                label="Expiring soon"
                value={data.items.filter((item) => item.expiryState === 'EXPIRING').length}
                meta="Decision required"
                tone="amber"
              />
              <MetricCard
                label="Production warnings"
                value={data.items.filter((item) => item.productionConsumptionCount > 0).length}
                meta="Experimental metric observed in use"
                tone="violet"
              />
            </section>
            <div className="sandbox-grid">
              {data.items.map((item) => (
                <article key={item.id} className="panel sandbox-card">
                  <div className="sandbox-card-head">
                    <span className="hex-mark">⬡</span>
                    <div>
                      <StatusBadge tone="violet">Sandbox</StatusBadge>
                      <CoverageBadge state={item.expiryState} />
                    </div>
                  </div>
                  <h2>{item.name}</h2>
                  <p>{item.purpose}</p>
                  <dl>
                    <div>
                      <dt>Owner</dt>
                      <dd>{item.ownerId.replaceAll('-', ' ')}</dd>
                    </div>
                    <div>
                      <dt>Expires</dt>
                      <dd>{formatDate(item.expiresAt)}</dd>
                    </div>
                    <div>
                      <dt>Audience</dt>
                      <dd>{item.audience.join(', ')}</dd>
                    </div>
                  </dl>
                  {item.productionConsumptionCount ? (
                    <div className="notice warning">
                      <WarningRegular />
                      <span>
                        Observed in {item.productionConsumptionCount} production consumer.
                      </span>
                    </div>
                  ) : null}
                  <div className="sandbox-actions">
                    <Button onClick={() => setSelected(item.id)}>View evidence</Button>
                    <Button
                      disabled={close.isPending || item.lifecycle !== 'ACTIVE'}
                      onClick={() => close.mutate({ id: item.id, action: 'archive' })}
                    >
                      Archive
                    </Button>
                    <Button
                      disabled={close.isPending || item.lifecycle !== 'ACTIVE'}
                      onClick={() => close.mutate({ id: item.id, action: 'dispose' })}
                    >
                      Dispose
                    </Button>
                    <Button
                      appearance="primary"
                      disabled={promote.isPending}
                      onClick={() => {
                        setSelected(item.id);
                        promote.mutate(item.id);
                      }}
                    >
                      Promote to draft
                    </Button>
                  </div>
                  {promote.isSuccess && selected === item.id ? (
                    <div className="notice success">
                      <CheckmarkCircleRegular />
                      Governed draft created. Approval was not inherited.
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

const agentInputModes: Array<{
  value: DaxPreviewRequest['inputMode'];
  label: string;
  description: string;
}> = [
  {
    value: 'NATURAL_LANGUAGE',
    label: 'Describe a variant',
    description: 'Use a configured AI provider to draft DAX from business intent.',
  },
  {
    value: 'DAX',
    label: 'Paste DAX',
    description: 'Validate and preview mature DAX without any AI integration.',
  },
];

function AgentConnectionDetails({
  config,
}: {
  config: Awaited<ReturnType<typeof queries.agentConfiguration>>;
}) {
  return (
    <section className="agent-status-grid" aria-label="AI model connection">
      <article className="panel agent-provider-card">
        <div className="agent-card-heading">
          <div>
            <span className="card-kicker">AI model connection</span>
            <h2>{config.provider.state.replaceAll('_', ' ')}</h2>
          </div>
          <StatusBadge tone={config.provider.state === 'READY' ? 'teal' : 'amber'}>
            {config.provider.kind.replaceAll('_', ' ')}
          </StatusBadge>
        </div>
        <dl className="agent-facts">
          <div>
            <dt>Authentication</dt>
            <dd>{config.provider.authMode.replaceAll('_', ' ')}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd>{config.provider.model ?? 'Not configured'}</dd>
          </div>
          <div>
            <dt>Credential</dt>
            <dd>{config.provider.credentialConfigured ? 'Server-side' : 'Not configured'}</dd>
          </div>
          <div>
            <dt>Live validation</dt>
            <dd>{config.provider.liveValidated ? 'Validated' : 'Not claimed'}</dd>
          </div>
        </dl>
        {config.provider.limitations.map((limitation) => (
          <div className="notice warning" key={limitation}>
            <WarningRegular />
            <span>{limitation}</span>
          </div>
        ))}
        <div className="notice success">
          <CheckmarkCircleRegular />
          <span>Manual DAX remains available without a provider or API key.</span>
        </div>
      </article>
      <article className="panel agent-provider-card">
        <span className="card-kicker">Secure enablement</span>
        <h2>Bring your model securely</h2>
        <div className="provider-option recommended">
          <strong>Microsoft Foundry + Entra ID</strong>
          <span>Recommended · managed identity or workload credentials</span>
          <code>{config.setup.foundry.requiredServerSettings.join(' · ')}</code>
        </div>
        <div className="provider-option">
          <strong>OpenAI-compatible endpoint</strong>
          <span>Inject the API key into the server secret store—never into this browser.</span>
          <code>{config.setup.custom.requiredServerSettings.join(' · ')}</code>
        </div>
        <p className="agent-config-note">
          Endpoint hosts must be allowlisted. Context is bounded to the confirmed model;
          credentials, raw result rows, and publication tools are excluded.
        </p>
      </article>
    </section>
  );
}

export function AgentPage() {
  const configuration = useQuery({
    queryKey: ['agent-configuration'],
    queryFn: queries.agentConfiguration,
  });
  const measures = useQuery({
    queryKey: ['agent-measures'],
    queryFn: () => queries.measures({ page: 1, pageSize: 100 }),
  });
  const [inputMode, setInputMode] = useState<DaxPreviewRequest['inputMode']>('NATURAL_LANGUAGE');
  const [input, setInput] = useState('');
  const [selectedMeasureId, setSelectedMeasureId] = useState('');
  const [confirmedModel, setConfirmedModel] = useState(false);
  const preview = useMutation({ mutationFn: queries.previewDax });
  const selectedMeasure =
    measures.data?.items.find(({ id }) => id === selectedMeasureId) ?? measures.data?.items[0];
  const mode = agentInputModes.find(({ value }) => value === inputMode)!;

  function chooseMode(value: DaxPreviewRequest['inputMode']) {
    setInputMode(value);
    setInput('');
    preview.reset();
  }

  return (
    <div className="page agent-page">
      <KpiManagementNav />
      <PageHeader
        eyebrow="KPI Management · controlled experimentation"
        title="Draft & Simulate"
        description="Describe a KPI variant or paste mature DAX, then validate and preview it without changing the production semantic model."
      />
      <QueryBoundary query={configuration}>
        {(config) => (
          <>
            <section className="panel agent-runtime-strip" aria-label="AI runtime status">
              <div>
                <span className="card-kicker">Optional drafting assistance</span>
                <strong>
                  AI provider {config.provider.state.replaceAll('_', ' ').toLocaleLowerCase()}
                </strong>
                <small>
                  {config.naturalLanguageAvailable
                    ? `${config.provider.model} is available for descriptive prompts.`
                    : 'Paste DAX remains available while AI connectivity is disabled or incomplete.'}
                </small>
              </div>
              <Link className="button-link" to="/connections">
                Manage AI connection <ArrowRightRegular />
              </Link>
            </section>

            <section className="panel agent-workbench">
              <div className="agent-workbench-heading">
                <div>
                  <span className="card-kicker">Preview and simulation</span>
                  <h2>Create a measure variant</h2>
                  <p>{mode.description}</p>
                </div>
                <LockClosedRegular aria-hidden />
              </div>
              <QueryBoundary query={measures}>
                {(measureData) => (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!selectedMeasure) return;
                      preview.mutate({
                        inputMode,
                        input,
                        sourceMeasureId: selectedMeasure.id,
                        semanticModelId: selectedMeasure.semanticModelId,
                        confirmedModel,
                      });
                    }}
                  >
                    <div className="agent-mode-switch" aria-label="DAX input mode">
                      {agentInputModes.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          className={inputMode === option.value ? 'active' : ''}
                          aria-pressed={inputMode === option.value}
                          onClick={() => chooseMode(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="agent-form-grid">
                      <label className="field">
                        <span>Source KPI / measure</span>
                        <select
                          value={selectedMeasure?.id ?? ''}
                          onChange={(event) => {
                            setSelectedMeasureId(event.target.value);
                            setConfirmedModel(false);
                            preview.reset();
                          }}
                        >
                          {measureData.items.map((measure) => (
                            <option key={measure.id} value={measure.id}>
                              {measure.name} · {measure.modelName ?? measure.semanticModelId}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="agent-model-context">
                        <span>Confirmed semantic context</span>
                        <strong>
                          {selectedMeasure?.modelName ?? selectedMeasure?.semanticModelId}
                        </strong>
                        <small>
                          Table {selectedMeasure?.tableName} · expression and in-model objects only
                        </small>
                      </div>
                    </div>
                    <label className="field agent-prompt-field">
                      <span>
                        {inputMode === 'DAX' ? 'DAX expression or query' : 'Describe the variant'}
                      </span>
                      <Textarea
                        resize="vertical"
                        rows={8}
                        value={input}
                        onChange={(_event, data) => setInput(data.value)}
                        placeholder={
                          inputMode === 'DAX'
                            ? 'Example: [Net Revenue] * 1.05'
                            : 'Example: Create a conservative Net Revenue scenario that applies a 5% downside adjustment.'
                        }
                      />
                    </label>
                    <label className="agent-confirmation">
                      <input
                        type="checkbox"
                        checked={confirmedModel}
                        onChange={(event) => setConfirmedModel(event.target.checked)}
                      />
                      <span>
                        I confirm this semantic model and understand the output is an ephemeral
                        draft, not a production model change.
                      </span>
                    </label>
                    {inputMode === 'NATURAL_LANGUAGE' && !config.naturalLanguageAvailable ? (
                      <div className="notice warning">
                        <InfoRegular />
                        <span>
                          Natural-language generation needs a configured server-side provider.
                          Switch to Paste DAX to continue without one.
                        </span>
                      </div>
                    ) : null}
                    <div className="workbench-actions">
                      <Button
                        type="submit"
                        appearance="primary"
                        icon={inputMode === 'DAX' ? <PlayRegular /> : <SparkleRegular />}
                        disabled={
                          !selectedMeasure ||
                          input.trim().length < 3 ||
                          !confirmedModel ||
                          preview.isPending
                        }
                      >
                        {preview.isPending
                          ? 'Preparing preview…'
                          : inputMode === 'DAX'
                            ? 'Validate and preview'
                            : 'Generate DAX and preview'}
                      </Button>
                    </div>
                  </form>
                )}
              </QueryBoundary>
              {preview.error ? <ErrorState error={preview.error} /> : null}
              {preview.data ? (
                <div className="agent-result" aria-live="polite">
                  <div className="agent-result-heading">
                    <div>
                      <span className="card-kicker">{preview.data.generatedBy} draft</span>
                      <h2>{preview.data.proposedName}</h2>
                    </div>
                    <StatusBadge
                      tone={preview.data.validation.state === 'FAILED' ? 'amber' : 'teal'}
                    >
                      Validation {preview.data.validation.state.toLocaleLowerCase()}
                    </StatusBadge>
                  </div>
                  <p>{preview.data.explanation}</p>
                  <div className="agent-code-grid">
                    <div>
                      <strong>Measure expression</strong>
                      <pre>
                        <code>{preview.data.measureExpression}</code>
                      </pre>
                    </div>
                    <div>
                      <strong>Query-scoped DAX</strong>
                      <pre>
                        <code>{preview.data.daxQuery}</code>
                      </pre>
                    </div>
                  </div>
                  {preview.data.validation.issues.map((issue) => (
                    <div
                      className={`notice ${issue.severity === 'ERROR' ? 'error' : 'warning'}`}
                      key={`${issue.code}-${issue.message}`}
                    >
                      <WarningRegular />
                      <span>{issue.message}</span>
                    </div>
                  ))}
                  {preview.data.execution.rows.length ? (
                    <div className="agent-preview-table">
                      <strong>Illustrative preview</strong>
                      <table>
                        <thead>
                          <tr>
                            {preview.data.execution.columns.map((column) => (
                              <th key={column.name}>{column.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.data.execution.rows.map((row) => (
                            <tr key={JSON.stringify(row)}>
                              {preview.data.execution.columns.map((column) => (
                                <td key={column.name}>{String(row[column.name] ?? '')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  {preview.data.provenance.warnings.map((warning) => (
                    <div className="notice warning" key={warning}>
                      <ShieldCheckmarkRegular />
                      <span>{warning}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

export function GovernancePage() {
  const governance = useQuery({ queryKey: ['governance'], queryFn: queries.governance });
  return (
    <div className="page">
      <PageHeader
        eyebrow="Measurable governance"
        title="Governance Health"
        description="See where definition, ownership, mapping, evidence, and lifecycle controls are improving—and exactly how each score is calculated."
      />
      <QueryBoundary query={governance}>
        {(data) => {
          return (
            <>
              <section className="governance-hero panel">
                <div className="estate-score">
                  <svg viewBox="0 0 140 140">
                    <circle cx="70" cy="70" r="57" />
                    <circle
                      className="progress"
                      cx="70"
                      cy="70"
                      r="57"
                      pathLength="100"
                      strokeDasharray={`${data.overallScore} 100`}
                    />
                  </svg>
                  <div>
                    <strong>{data.overallScore}</strong>
                    <span>Estate health</span>
                  </div>
                </div>
                <div>
                  <h2>Current registry control coverage</h2>
                  <p>
                    This score is calculated from current governed definitions, owners, validated
                    mappings, observed report evidence, and sandbox expiry state.
                  </p>
                  <div className="maturity-callout">
                    <span>
                      Fabric governance maturity {data.maturity.level} · {data.maturity.label}
                    </span>
                    <p>{data.maturity.narrative}</p>
                  </div>
                </div>
              </section>
              {data.priorityActions.length ? (
                <section className="panel governance-action-plan">
                  <SectionTitle
                    title="Priority action plan"
                    action={<StatusBadge tone="warning">Next 3 controls</StatusBadge>}
                  />
                  <div className="action-plan-grid">
                    {data.priorityActions.map((action) => (
                      <article key={action.key}>
                        <div>
                          <span>Target maturity {action.targetMaturityLevel}</span>
                          <strong>{action.title}</strong>
                        </div>
                        <p>{action.impact}</p>
                        <small>
                          Gap: <b>{action.gap}</b> record{action.gap === 1 ? '' : 's'} · current{' '}
                          <b>{action.score}%</b>
                        </small>
                        <Link className="text-link" to={action.route}>
                          {action.nextStep} <ArrowRightRegular />
                        </Link>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
              <section className="score-grid">
                {data.scores.map((score) => (
                  <article key={score.key} className="panel score-card">
                    <div>
                      <span>{score.label}</span>
                      <strong>{score.score}%</strong>
                    </div>
                    <ProgressBar
                      value={score.score / 100}
                      color={score.score < 60 ? 'warning' : 'brand'}
                    />
                    <p>
                      <b>{score.numerator}</b> of <b>{score.denominator}</b> eligible records meet
                      the control.
                    </p>
                  </article>
                ))}
              </section>
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

export function ConnectionsPage() {
  const capabilities = useQuery({ queryKey: ['capabilities'], queryFn: queries.capabilities });
  const agentConfiguration = useQuery({
    queryKey: ['agent-configuration'],
    queryFn: queries.agentConfiguration,
  });
  const [workspaceText, setWorkspaceText] = useState(
    'Corporate Finance\nExecutive Analytics\nRegional Sales',
  );
  const [resolved, setResolved] = useState<ResolutionView[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [preflight, setPreflight] = useState<Awaited<ReturnType<typeof queries.preflight>> | null>(
    null,
  );
  const [scanJob, setScanJob] = useState<ScanJob | null>(null);
  const resolveMutation = useMutation({
    mutationFn: queries.resolve,
    onSuccess: ({ items }) => {
      setResolved(items);
      setPreflight(null);
      setChosen(
        Object.fromEntries(
          items
            .filter((item) => item.workspaceId)
            .map((item) => [item.inputName, item.workspaceId!]),
        ),
      );
    },
  });
  const selectedIds = resolved
    .map((item) => chosen[item.inputName] ?? item.workspaceId)
    .filter((id): id is string => Boolean(id));
  const preflightMutation = useMutation({ mutationFn: queries.preflight, onSuccess: setPreflight });
  const createMutation = useMutation({
    mutationFn: async () => {
      const created = await queries.createScope({
        name: 'Enterprise KPI scope',
        tenantId: 'synthetic-tenant',
        workspaceIds: selectedIds,
        inputNames: resolved
          .filter((item) => selectedIds.includes(chosen[item.inputName] ?? item.workspaceId ?? ''))
          .map((item) => item.inputName),
        externalDependencyPolicy: 'PROMPT_TO_EXPAND',
      });
      return queries.startScan(created.item.id);
    },
    onSuccess: ({ item }) => setScanJob(item),
  });
  const scan = useQuery({
    queryKey: ['scan', scanJob?.id],
    queryFn: () => queries.scan(scanJob!.id),
    enabled: Boolean(
      scanJob &&
      !['COMPLETED', 'COMPLETED_WITH_GAPS', 'FAILED', 'CANCELLED'].includes(scanJob.status),
    ),
    refetchInterval: (query) => {
      const status = query.state.data?.item.status;
      return status && ['COMPLETED', 'COMPLETED_WITH_GAPS', 'FAILED', 'CANCELLED'].includes(status)
        ? false
        : 700;
    },
  });
  const currentScan = scan.data?.item ?? scanJob;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Administration · integrations"
        title="Connections"
        description="Configure and verify the infrastructure connections used for Fabric metadata, semantic queries, and optional AI-assisted DAX drafting."
      />
      <section className="connection-section-heading">
        <span className="card-kicker">Data estate</span>
        <h2>Microsoft Fabric</h2>
        <p>Resolve the governed workspace scope and inspect read-only capability coverage.</p>
      </section>
      <QueryBoundary query={capabilities}>
        {(data) => (
          <div className="connection-layout">
            <section className="panel setup-panel">
              <div className="step-heading">
                <span>1</span>
                <div>
                  <h2>Choose Fabric workspaces</h2>
                  <p>
                    Enter one workspace display name per line. Stable IDs become the durable
                    boundary after resolution.
                  </p>
                </div>
              </div>
              <label className="field">
                <span>Workspace names</span>
                <Textarea
                  rows={6}
                  value={workspaceText}
                  onChange={(_, value) => setWorkspaceText(value.value)}
                />
              </label>
              <div className="scope-note">
                <LockClosedRegular />
                <span>
                  <strong>Read-only by design.</strong> MetricGraph inventories metadata, evidence,
                  and lineage. It does not copy report data or write to semantic models.
                </span>
              </div>
              <Button
                appearance="primary"
                icon={<DatabaseSearchRegular />}
                disabled={resolveMutation.isPending}
                onClick={() =>
                  resolveMutation.mutate(
                    workspaceText
                      .split('\n')
                      .map((name) => name.trim())
                      .filter(Boolean),
                  )
                }
              >
                {resolveMutation.isPending ? 'Resolving…' : 'Resolve and check access'}
              </Button>
              {resolveMutation.error ? <ErrorState error={resolveMutation.error} /> : null}
            </section>
            <aside className="panel capability-panel">
              <div className="provider-heading">
                <span className="provider-mark">F</span>
                <div>
                  <strong>Microsoft Fabric</strong>
                  <small>{data.provider} provider · current runtime</small>
                </div>
                <CoverageBadge state={data.liveTenantValidated ? 'COMPLETE' : 'PARTIAL'} />
              </div>
              <div className="capability-list">
                {data.capabilities.slice(0, 5).map((capability) => (
                  <div key={capability.capability}>
                    <span
                      className={
                        capability.state === 'AVAILABLE' ? 'cap-icon ready' : 'cap-icon partial'
                      }
                    >
                      {capability.state === 'AVAILABLE' ? (
                        <CheckmarkCircleRegular />
                      ) : (
                        <WarningRegular />
                      )}
                    </span>
                    <span>
                      <strong>{capability.capability.replaceAll('_', ' ')}</strong>
                      <small>
                        {capability.identityClass.replaceAll('_', ' ').toLowerCase()} ·{' '}
                        {capability.limitation ?? 'Available in the active provider.'}
                      </small>
                    </span>
                  </div>
                ))}
              </div>
              <div className="trust-plane-list" aria-label="Operation identity boundaries">
                <strong>Operation identities</strong>
                {data.identityPolicies.map((policy) => (
                  <div key={policy.trustPlane}>
                    <span>{policy.trustPlane.replaceAll('_', ' ')}</span>
                    <small>
                      {policy.allowedIdentityClasses
                        .map((identity) => identity.replaceAll('_', ' ').toLowerCase())
                        .join(', ')}
                      {policy.interactiveUserRequired ? ' · delegated user required' : ''}
                      {!policy.enabled ? ' · disabled' : ''}
                    </small>
                  </div>
                ))}
              </div>
              <p className="coverage-note">
                <InfoRegular /> Synthetic provider proves the complete workflow locally. Live tenant
                validation is pending credentials and tenant consent.
              </p>
            </aside>
          </div>
        )}
      </QueryBoundary>
      {resolved.length ? (
        <section className="panel resolution-panel">
          <div className="step-heading">
            <span>2</span>
            <div>
              <h2>Confirm resolved workspace IDs</h2>
              <p>
                Ambiguous names require an explicit choice. Renames will not silently change this
                scope.
              </p>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Input name</th>
                  <th>Resolution</th>
                  <th>Stable workspace ID</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {resolved.map((item) => (
                  <tr key={item.inputName}>
                    <td>
                      <strong>{item.inputName}</strong>
                    </td>
                    <td>
                      {item.status === 'AMBIGUOUS' ? (
                        <select
                          aria-label={`Select ${item.inputName} workspace`}
                          value={chosen[item.inputName] ?? ''}
                          onChange={(event) =>
                            setChosen((value) => ({
                              ...value,
                              [item.inputName]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Choose exact workspace…</option>
                          {item.candidates.map((candidate) => (
                            <option key={candidate.workspaceId} value={candidate.workspaceId}>
                              {candidate.displayName} · …{candidate.workspaceId.slice(-4)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        item.displayName
                      )}
                    </td>
                    <td>
                      <code>{chosen[item.inputName] ?? item.workspaceId ?? 'Not resolved'}</code>
                    </td>
                    <td>
                      <StatusBadge
                        tone={
                          item.status === 'MATCHED'
                            ? 'success'
                            : item.status === 'AMBIGUOUS'
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {item.status}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="resolution-actions">
            <span>
              {selectedIds.length} of {resolved.length} selected
            </span>
            <Button
              appearance="primary"
              disabled={selectedIds.length !== resolved.length || preflightMutation.isPending}
              onClick={() => preflightMutation.mutate(selectedIds)}
            >
              {preflightMutation.isPending ? 'Checking…' : 'Run capability preflight'}
            </Button>
          </div>
        </section>
      ) : null}
      {preflight ? (
        <section className="panel preflight-panel">
          <div className="step-heading">
            <span>3</span>
            <div>
              <h2>Review expected scan coverage</h2>
              <p>
                Proceed with explicit limitations; incomplete visual evidence remains visible
                throughout classification.
              </p>
            </div>
          </div>
          <section className="metric-grid compact">
            <MetricCard
              label="Workspaces"
              value={preflight.counts.workspaces}
              meta="Resolved root scope"
            />
            <MetricCard
              label="Semantic models"
              value={preflight.counts.semanticModels}
              meta="Metadata inventory"
              tone="blue"
            />
            <MetricCard
              label="Reports"
              value={preflight.counts.reports}
              meta={`${preflight.counts.visualUsageReports} with visual usage`}
              tone="amber"
            />
          </section>
          <div className="preflight-actions">
            <div>
              <CoverageBadge
                state={
                  preflight.counts.accessibleReports === preflight.counts.reports
                    ? 'COMPLETE'
                    : 'PARTIAL'
                }
              />
              <span>
                {preflight.counts.accessibleReports} of {preflight.counts.reports} reports
                accessible
              </span>
            </div>
            <Button
              appearance="primary"
              icon={<PlayRegular />}
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Creating scope…' : 'Start initial scan'}
            </Button>
          </div>
        </section>
      ) : null}
      {currentScan ? (
        <section className="panel scan-panel" aria-live="polite">
          <div className="scan-header">
            <div className="scan-spinner">
              <ArrowSyncRegular />
            </div>
            <div>
              <span className="card-kicker">Asynchronous scan · safe to leave</span>
              <h2>{currentScan.stage.replaceAll('_', ' ').toLowerCase()}</h2>
              <p>
                {currentScan.status === 'COMPLETED_WITH_GAPS'
                  ? 'Discovery completed with explicit coverage limitations.'
                  : 'MetricGraph is projecting evidence into the registry and graph.'}
              </p>
            </div>
            <strong>{currentScan.progress}%</strong>
          </div>
          <ProgressBar value={currentScan.progress / 100} />
          <div className="scan-stages">
            {[
              'Scope',
              'Inventory',
              'Semantic metadata',
              'Visual usage',
              'Graph',
              'Classify',
              'Review cases',
            ].map((stage, index) => (
              <span key={stage} className={currentScan.progress >= (index + 1) * 13 ? 'done' : ''}>
                {currentScan.progress >= (index + 1) * 13 ? <CheckmarkCircleRegular /> : <i />}
                {stage}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      <section className="connection-section-heading ai-connection-heading">
        <span className="card-kicker">Drafting infrastructure</span>
        <h2>AI model</h2>
        <p>Configure the optional model connection separately from KPI drafting and simulation.</p>
      </section>
      <QueryBoundary query={agentConfiguration}>
        {(config) => <AgentConnectionDetails config={config} />}
      </QueryBoundary>
    </div>
  );
}

export function SettingsPage() {
  return (
    <div className="page">
      <PageHeader
        eyebrow="Administration"
        title="Settings"
        description="Review the policy posture enforced by this MetricGraph deployment."
      />
      <div className="settings-layout">
        <section className="panel settings-panel">
          <SectionTitle title="Active policy posture" />
          <div className="settings-row">
            <div>
              <strong>Require rationale for every decision</strong>
              <p>Review and approval actions cannot be recorded without an explanatory reason.</p>
            </div>
            <StatusBadge tone="success">Enforced</StatusBadge>
          </div>
          <div className="settings-row">
            <div>
              <strong>Fail closed on temporal ambiguity</strong>
              <p>Block active-definition resolution when variants overlap at equal precedence.</p>
            </div>
            <StatusBadge tone="success">Fail closed</StatusBadge>
          </div>
          <div className="settings-row">
            <div>
              <strong>Reduced motion by default</strong>
              <p>Disable graph fly-to animation unless a user explicitly enables it.</p>
            </div>
            <StatusBadge>System preference</StatusBadge>
          </div>
        </section>
      </div>
    </div>
  );
}
